import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { cn } from "../ui/cn";

/**
 * Put a company on a ticket that has none.
 *
 * Ingest used to do this by itself: an unknown business domain became a stub
 * company and the contact was filed onto it. That filled the CRM with accounts
 * nobody had -- couriers, recruiters, a supplier's dispatch robot -- so it was
 * removed, and this is what replaces it. A person decides, with the email in
 * front of them, which is the only place the information to decide exists.
 *
 * **It files the contact, because that is what a ticket's company is.** There
 * is no `company_id` on a ticket: `TicketsService` reads it through
 * `contacts.company_id`, so one control here would otherwise be a second way
 * of saying the same thing and a second place for it to disagree. The
 * consequence is worth being plain about -- filing from this ticket files the
 * person, so their other tickets show the company too. That is almost always
 * what somebody means, and it is the whole value of doing it once.
 *
 * Creating is offered rather than being a dead end. The CRM's unfiled queue --
 * the page this control replaced -- said "create it from the Companies page
 * first", which was tolerable while ingest created most companies itself and is
 * not now that nothing does: an agent who has to leave the ticket, make a
 * record, and come back will file nothing at all.
 *
 * Always with their history. These contacts have no company for it to have been
 * left at, so the question the CRM's move dialog asks -- filed wrongly, or
 * changed employer -- has only one answer here.
 */
export function AddCompany({
  contactId,
  contactEmail,
  ticketId,
}: {
  contactId: number;
  /** For the domain, which is both the best search and the best first guess. */
  contactEmail: string | null;
  ticketId: number;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-brand-600 underline decoration-brand-300 underline-offset-2 hover:text-brand-700 dark:text-brand-400"
      >
        Add company
      </button>
    );
  }

  return (
    <Picker
      contactId={contactId}
      contactEmail={contactEmail}
      ticketId={ticketId}
      onDone={() => setOpen(false)}
    />
  );
}

function Picker({
  contactId,
  contactEmail,
  ticketId,
  onDone,
}: {
  contactId: number;
  contactEmail: string | null;
  ticketId: number;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();

  /*
   * Opens on the sender's domain rather than empty. It is the one thing known
   * about this person that names their employer, the CRM's search matches a
   * company by it, and it is what makes the common case -- an account we
   * already hold, under a name nobody would have guessed -- a single keystroke
   * of nothing.
   */
  const domain = domainOf(contactEmail);
  const [term, setTerm] = useState(domain ?? "");

  const results = useQuery({
    queryKey: queryKeys.crmSearch(term.trim()),
    queryFn: () => crmApi.search(term.trim()),
    enabled: term.trim().length >= 2,
    staleTime: 30_000,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.ticket(ticketId) });
    /* The queue rows carry the company too, and the CRM's own lists have just
       gained a contact or a record. */
    void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompaniesRoot });
    onDone();
  };

  const file = useMutation({
    mutationFn: (companyId: number) =>
      crmApi.moveContact(contactId, companyId, true),
    onSuccess: refresh,
  });

  const create = useMutation({
    mutationFn: async () => {
      const company = await crmApi.createCompany({
        name: term.trim(),
        /* The domain the mail came from, not the box's text -- somebody who
           typed a name over the top still wants the record to match the next
           person who writes in from there. Nothing is auto-created any more,
           so this is the record's only chance to get its domain. */
        ...(domain ? { domain } : {}),
      });
      await crmApi.moveContact(contactId, company.id, true);
    },
    onSuccess: refresh,
  });

  const rows = results.data?.results ?? [];
  /*
   * Offered as soon as there is a name to create, not only when the search
   * came back empty. CRM search is fuzzy -- "brightbikes.test" returns Acme
   * Networks on the dev seed -- so gating on nought results meant one
   * irrelevant match made creating unreachable, and the agent's way out was
   * to type something they knew would match nothing. It sits under the
   * results so they are read first.
   */
  const searched = term.trim().length >= 2 && !results.isLoading;
  const busy = file.isPending || create.isPending;
  const failure = file.error ?? create.error;

  return (
    <div className="min-w-0 flex-1 rounded-md border border-line bg-app p-2">
      <input
        value={term}
        autoFocus
        onChange={(event) => setTerm(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") onDone();
        }}
        placeholder="Which company?"
        aria-label="Search for the company this ticket belongs to"
        className="h-8 w-full rounded-md border border-line bg-surface px-2 text-xs text-content outline-none focus:border-brand-400"
      />

      {failure ? (
        <p className="mt-1 text-[11px] text-sla-breached">
          {failure instanceof ApiError
            ? failure.message
            : "Could not file that."}
        </p>
      ) : null}

      <ul className="mt-1 max-h-40 overflow-y-auto">
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => file.mutate(row.id)}
              className={cn(
                "flex w-full flex-col rounded px-2 py-1 text-left hover:bg-surface-2",
                busy && "opacity-50",
              )}
            >
              <span className="text-xs text-content">{row.name}</span>
              <span className="text-[11px] text-faint">
                {[row.domain, row.city, row.tradeAccountNumber]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {/*
        The way out when the account is genuinely new, which since ingest
        stopped inventing companies is an ordinary morning rather than an
        exception. It makes the record and files the contact in one press --
        the details somebody would fill in on the Companies page can be added
        later from the company itself, and the alternative is leaving the
        ticket to do it.
      */}
      {searched ? (
        <div className="border-t border-line px-2 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => create.mutate()}
            className="text-[11px] text-brand-600 underline decoration-brand-300 underline-offset-2 hover:text-brand-700 disabled:opacity-50 dark:text-brand-400"
          >
            {create.isPending
              ? "Creating…"
              : `${rows.length === 0 ? "No match" : "None of these"} — create “${term.trim()}”${domain ? ` (${domain})` : ""}`}
          </button>
        </div>
      ) : null}

      <button
        type="button"
        onClick={onDone}
        className="mt-1 px-2 text-[11px] text-faint hover:text-muted"
      >
        Cancel
      </button>
    </div>
  );
}

/** The bit after the @, lowercased, or null for anything unusable. */
function domainOf(email: string | null): string | null {
  const at = email?.lastIndexOf("@") ?? -1;
  if (at < 1) return null;

  const domain = email!.slice(at + 1).trim().toLowerCase();
  return domain.includes(".") ? domain : null;
}
